/**
 * loaderNormalJson_Api.ts - API support helpers for LoaderNormalJson.
 * Holds the private serialization helpers used by getTemplatesApiResponse.
 */

import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { AppSiteTemplateData } from '../../../../Assembler/dist/model/appSiteTemplateData.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { LoaderJsonHelper } from '../../base/loader/loaderJsonHelper.js';
import type { IConfig } from '../../../../Assembler/dist/config/iConfig.js';
import type { LoaderNormalJson } from './loaderNormalJson.js';

/**
 * Internal cache entry type for template data during API response building
 */
interface TemplateDataEntry {
    htmlJson: JsonObject | null;
    dataJson: JsonObject | null;
}

/**
 * Cache entry stored in htmlJsonCache / dataJsonCache
 */
interface CacheEntry {
    relativePath: string;
    jsonStructure: JsonObject;
}

type TemplateCache = Map<string, Map<string, CacheEntry>>;

/**
 * Gets all templates as dictionary of AppSite to template data.
 */
export function getAllTemplatesJson(
    loader: LoaderNormalJson,
    htmlJsonCache: TemplateCache,
    dataJsonCache: TemplateCache,
    primaryAppSite: string,
    config: IConfig,
    includeSearchAppSites: boolean = true,
    maskHtml: boolean = false,
    orderedTemplateNames: string[] | null = null
): Record<string, AppSiteTemplateData> {
    const result: Record<string, AppSiteTemplateData> = {};
    const searchAppSites = includeSearchAppSites ? loader.getSearchAppSitesForAppSite(primaryAppSite) : '';
    const appSitesToInclude = (loader.constructor as typeof LoaderNormalJson)._buildAppSitesToLoad(primaryAppSite, searchAppSites);

    for (const appSite of appSitesToInclude) {
        if (!htmlJsonCache.has(appSite) && !dataJsonCache.has(appSite)) continue;
        const templatesJson = loader.buildTemplatesJsonForAppSite(htmlJsonCache, dataJsonCache, appSite, maskHtml, orderedTemplateNames);
        result[appSite] = new AppSiteTemplateData(appSite, templatesJson, null, loader.getAppFile(appSite, '').appFile);
    }

    return result;
}

export const apiMethods = {
    buildTemplatesJsonForAppSite(
        this: LoaderNormalJson,
        htmlJsonCache: TemplateCache,
        dataJsonCache: TemplateCache,
        appSite: string,
        maskHtml: boolean = false,
        orderedTemplateNames: string[] | null = null
    ): string {
        const templateData: Record<string, TemplateDataEntry> = {};

        const htmlTemplates = htmlJsonCache.get(appSite);
        if (htmlTemplates) {
            for (const [templateName, htmlFile] of htmlTemplates.entries()) {
                const templateKey = `${appSite}_${templateName}`;
                templateData[templateKey] = { htmlJson: htmlFile.jsonStructure, dataJson: null };
            }
        }

        const dataTemplates = dataJsonCache.get(appSite);
        if (dataTemplates) {
            for (const [templateKey, dataFile] of dataTemplates.entries()) {
                const lowerKey = templateKey.toLowerCase();
                const existingKey = Object.keys(templateData).find(k => k.toLowerCase() === lowerKey);
                if (existingKey) {
                    templateData[existingKey].dataJson = dataFile.jsonStructure;
                } else {
                    templateData[templateKey] = { htmlJson: null, dataJson: dataFile.jsonStructure };
                }
            }
        }

        let templateKeys: string[];
        if (orderedTemplateNames && orderedTemplateNames.length > 0) {
            templateKeys = [];
            const allKeys = Object.keys(templateData);
            const addedKeys = new Set<string>();

            for (const orderedName of orderedTemplateNames) {
                const componentName = orderedName.startsWith('!') ? orderedName.slice(1) : orderedName;
                const componentLower = componentName.toLowerCase();
                for (const key of allKeys) {
                    if (addedKeys.has(key)) continue;
                    const keyLower = key.toLowerCase();
                    if (keyLower === componentLower || keyLower.endsWith(`_${componentLower}`)) {
                        templateKeys.push(key);
                        addedKeys.add(key);
                        break;
                    }
                }
            }
        } else {
            templateKeys = Object.keys(templateData);
        }

        const sb: string[] = [];
        sb.push('{');

        let first = true;
        for (const key of templateKeys) {
            if (!first) sb.push(',');
            first = false;

            const data = templateData[key];
            sb.push('"');
            sb.push(JsonUtility.escapeJson(key));
            sb.push('":{"HtmlJson":');

            if (data.htmlJson) {
                // Emit HtmlJson as an OPAQUE byte artifact via the single shared encoder, so
                // ConfigClient and ConfigOCI receive byte-identical artifacts (format owned by loader).
                LoaderJsonHelper.appendEncodedArtifact(sb, data.htmlJson, maskHtml);
            } else {
                sb.push('null');
            }

            sb.push(',"DataJson":');
            if (data.dataJson) {
                sb.push(JsonUtility.serializeObject(data.dataJson));
            } else {
                sb.push('null');
            }
            sb.push('}');
        }

        sb.push('}');
        return sb.join('');
    }
};
